import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Plus } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Tag } from '@creditornot/cb-tag';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { DecodedValueMap } from 'use-query-params';

import { fetchPurchasesTotals } from 'apis/merchant-gateway/merchant-analytics/api';
import { isAnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { localize } from 'common/utils';

import BarcodesModal from './BarcodesModal';
import { Period } from './FilterDateRangePicker';
import FiltersContent from './FiltersContent';
import PurchaseItemsTable from './PurchaseItemsTable';
import PurchaseOrders from './PurchaseOrders';
import PurchasesTableTotals from './PurchasesTableTotals';
import { PurchasesQueryConfig, VIEW_TYPE } from './purchasesQueryConfig';
import {
  telemetryMerchantAdminPurchasesInteractionSwitchToItems,
  telemetryMerchantAdminPurchasesInteractionSwitchToOrders,
  useTelemetryMerchantAdminPurchasesViewTracking,
} from './telemetry';

const PurchasesContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const SummaryHeader = styled.div`
  display: flex;
  padding: 16px 0 4px;
  flex-direction: row;
  align-items: center;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const Totals = styled(PurchasesTableTotals)`
  padding-inline: 24px 16px;
  border-inline-end: 1px solid ${cbSemanticCssVars.borderPrimary};

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    padding-inline: 24px;
    border-inline-end: none;
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    padding-block-start: 16px;
    margin-block-start: 16px;
  }
`;

const Filters = styled.div`
  padding-inline: 16px 24px;
  display: flex;
  align-items: center;
  gap: 12px 16px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    padding-inline: 24px;
  }
`;

const FilterButtonWrapper = styled.div`
  position: relative;
`;

// Hidden button to control the position of the filter popup to reduce jumping on filter count change
const FilterButtonWidthControl = styled(Button)`
  visibility: hidden;
`;

const FilterButton = styled(Button)`
  position: absolute;
  left: 0;
  top: 0;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

type PurchasesPageProps = {
  venue: Venue;
  dateRange: {
    from: string;
    to: string;
  };
  isBarCodesModalOpen: boolean;
  isDateRangeIntersectsWithAllowedRange: boolean;
  query: DecodedValueMap<PurchasesQueryConfig>;
  setQuery: (query: DecodedValueMap<PurchasesQueryConfig>) => void;
  handleDateRangeChange: (period: Period) => void;
  setIsBarCodesModalOpen: (isOpen: boolean) => void;
};

const PurchasesPage = ({
  isBarCodesModalOpen,
  venue,
  dateRange,
  query,
  isDateRangeIntersectsWithAllowedRange,
  setQuery,
  setIsBarCodesModalOpen,
}: PurchasesPageProps) => {
  const intl = useIntl();
  useTelemetryMerchantAdminPurchasesViewTracking();

  const { data: purchasesTotals, isLoading } = useQuery(
    [`/merchant-analytics-api/v2/venues/${venue.id}/purchases/totals`, dateRange, query.filters],
    () => {
      if (!dateRange?.from || !dateRange.to || isDateRangeIntersectsWithAllowedRange) {
        return undefined;
      }

      return fetchPurchasesTotals({
        venueId: venue.id,
        startDate: dateRange.from,
        endDate: DateTime.fromISO(dateRange.to).plus({ days: 1 }).toISODate(),
        filters: query.filters?.split(',').filter(isAnalyticsFilter),
      });
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
    },
  );

  const handleChangeView = (value: VIEW_TYPE) => {
    setQuery({ view: value, filters: undefined, purchaseId: undefined, itemId: undefined });
  };

  const viewToggleOptions: { label: string; value: VIEW_TYPE }[] = [
    {
      label: `${intl.formatMessage({ id: 'purchases.tabs.orders' })} (${purchasesTotals?.count || 0})`,
      value: VIEW_TYPE.orders,
    },
    {
      label: intl.formatMessage({ id: 'purchases.tabs.items' }),
      value: VIEW_TYPE.items,
    },
  ];

  const filterCount = query.filters?.split(',').length;

  const [showFees, setShowFees] = useState(!!purchasesTotals?.count && purchasesTotals.feesDelivered.gross > 0);
  useEffect(() => {
    // Update showing fees logic only when there are purchases
    if (!isLoading && purchasesTotals?.count) {
      setShowFees(purchasesTotals.feesDelivered.gross > 0);
    }
  }, [purchasesTotals, isLoading]);

  return (
    <PurchasesContainer>
      <SummaryHeader>
        <Totals purchasesTotals={purchasesTotals} showFees={showFees} currency={venue.currency} isLoading={isLoading} />

        <Filters>
          <SegmentedControl
            // when label is dynamically changed (e.g. count, or language change), component layout gets broken.
            // we use key to force re-render and fix the layout.
            key={viewToggleOptions.map(option => option.label).join()}
            onChange={value => {
              if (value === VIEW_TYPE.orders) {
                telemetryMerchantAdminPurchasesInteractionSwitchToOrders();
              } else if (value === VIEW_TYPE.items) {
                telemetryMerchantAdminPurchasesInteractionSwitchToItems();
              }
              handleChangeView(value);
            }}
            options={viewToggleOptions}
            value={query.view}
            disabled={isDateRangeIntersectsWithAllowedRange}
          />

          <Popover
            content={({ close }) => <FiltersContent setQuery={setQuery} query={query} onClose={close} />}
            renderArrow={false}
            placement="bottom"
            offset={8}
          >
            {({ refProps }) => (
              <FilterButtonWrapper {...refProps}>
                <FilterButtonWidthControl
                  variant="lightBlue"
                  size="small"
                  icon={
                    <Tag variant="blue" size="tiny">
                      10
                    </Tag>
                  }
                  tabIndex={-1}
                >
                  <FormattedMessage id="purchases.labels.filter" />
                </FilterButtonWidthControl>
                <FilterButton
                  variant="lightBlue"
                  size="small"
                  icon={
                    !filterCount ? (
                      <Plus />
                    ) : (
                      <Tag variant="blue" size="tiny">
                        {filterCount}
                      </Tag>
                    )
                  }
                  iconPosition="right"
                >
                  <FormattedMessage id="purchases.labels.filter" />
                </FilterButton>
              </FilterButtonWrapper>
            )}
          </Popover>
        </Filters>
      </SummaryHeader>

      {!isDateRangeIntersectsWithAllowedRange && (
        <>
          {dateRange && query.view === VIEW_TYPE.orders && (
            <PurchaseOrders dateRange={dateRange} query={query} setQuery={setQuery} showFees={showFees} venue={venue} />
          )}
          {dateRange && query.view === VIEW_TYPE.items && (
            <PurchaseItemsTable dateRange={dateRange} query={query} setQuery={setQuery} venue={venue} />
          )}
        </>
      )}

      {isDateRangeIntersectsWithAllowedRange && (
        <ErrorAlert variant="error">
          <FormattedMessage id="purchase-history.date-out-of-range" />
        </ErrorAlert>
      )}
      {dateRange && isBarCodesModalOpen && (
        <BarcodesModal
          range={{
            from: DateTime.fromISO(dateRange.from, { zone: venue.timezone }).toISO(),
            to: DateTime.fromISO(dateRange.to, { zone: venue.timezone }).plus({ days: 1 }).toISO(),
          }}
          venueId={venue.id}
          venueName={localize(venue.name, intl.locale)}
          onClose={() => setIsBarCodesModalOpen(false)}
        />
      )}
    </PurchasesContainer>
  );
};

export default PurchasesPage;
